import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import {
  CheckCircle2,
  AlertCircle,
  Save,
  Send,
  Loader2,
  Info,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Tooltip } from "../../ui/tooltip";
import {
  EmailSettings,
  EmailProvider,
  EmailTemplateKey,
  GET_EMAIL_SETTINGS,
  SEND_TEST_EMAIL,
  UPDATE_EMAIL_SETTINGS,
} from "../../../graphql/email";

const TEMPLATE_OPTIONS: Array<{ value: EmailTemplateKey; label: string }> = [
  { value: "PROJECT_MEMBER_ADDED", label: "Dodanie do projektu" },
  { value: "TASK_ASSIGNED", label: "Przypisanie zadania" },
  { value: "PROJECT_STATUS_CHANGED", label: "Zmiana statusu projektu" },
  { value: "TASK_STATUS_CHANGED", label: "Zmiana statusu zadania" },
  { value: "TASK_COMMENT_ADDED", label: "Nowy komentarz w zadaniu" },
];

interface EmailConfigTabProps {
  settings: EmailSettings;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// SMTP is intentionally hidden in the UI for now: Railway blocks outbound SMTP
// (free/hobby plans), so this deployment sends via Resend (HTTPS). The whole SMTP
// backend + UI stays in place — flip this to `true` to bring SMTP back (e.g. for a
// client whose hosting allows outbound SMTP, or on a Railway Pro plan).
const SHOW_SMTP = false;

export const EmailConfigTab: React.FC<EmailConfigTabProps> = ({ settings }) => {
  const [enabled, setEnabled] = useState<boolean>(settings.enabled);
  const [provider, setProvider] = useState<EmailProvider>(
    SHOW_SMTP ? settings.provider : "RESEND"
  );
  const [fromAddress, setFromAddress] = useState<string>(settings.fromAddress);
  const [fromName, setFromName] = useState<string>(settings.fromName);
  const [smtpHost, setSmtpHost] = useState<string>(settings.smtpHost || "");
  const [smtpPort, setSmtpPort] = useState<number>(settings.smtpPort || 587);
  const [smtpSecure, setSmtpSecure] = useState<boolean>(settings.smtpSecure);
  const [smtpUser, setSmtpUser] = useState<string>(settings.smtpUser || "");
  // Password is write-only: we never receive it back, so the field starts empty.
  // Empty = leave existing password untouched; typed value = save as new password.
  const [smtpPassword, setSmtpPassword] = useState<string>("");

  // Test email form
  const [testEmail, setTestEmail] = useState<string>("");
  const [testTemplate, setTestTemplate] = useState<string>("none");

  useEffect(() => {
    setEnabled(settings.enabled);
    setProvider(SHOW_SMTP ? settings.provider : "RESEND");
    setFromAddress(settings.fromAddress);
    setFromName(settings.fromName);
    setSmtpHost(settings.smtpHost || "");
    setSmtpPort(settings.smtpPort || 587);
    setSmtpSecure(settings.smtpSecure);
    setSmtpUser(settings.smtpUser || "");
    setSmtpPassword("");
  }, [settings]);

  const [updateSettings, { loading: saving }] = useMutation(UPDATE_EMAIL_SETTINGS, {
    refetchQueries: [{ query: GET_EMAIL_SETTINGS }],
    onCompleted: () => {
      toast.success("Konfiguracja maili zaktualizowana");
    },
    onError: (error) => {
      toast.error(`Błąd zapisu: ${error.message}`);
    },
  });

  const [sendTest, { loading: sending }] = useMutation(SEND_TEST_EMAIL, {
    onCompleted: (data) => {
      const logId = data?.sendTestEmail?.logId;
      toast.success(
        logId
          ? `Wysłano testowy mail (log #${String(logId).slice(0, 8)}…)`
          : "Wysłano testowy mail"
      );
    },
    onError: (error) => {
      toast.error(`Błąd wysyłania: ${error.message}`);
    },
  });

  const fromAddressInvalid =
    fromAddress.trim() !== "" && !EMAIL_REGEX.test(fromAddress.trim());
  const fromAddressEmpty = fromAddress.trim() === "";
  const fromNameEmpty = fromName.trim() === "";
  const smtpHostEmpty = provider === "SMTP" && smtpHost.trim() === "";
  const smtpPortInvalid =
    provider === "SMTP" && (smtpPort < 1 || smtpPort > 65535);

  const formInvalid =
    fromAddressInvalid ||
    fromAddressEmpty ||
    fromNameEmpty ||
    smtpHostEmpty ||
    smtpPortInvalid;

  const hasChanges =
    enabled !== settings.enabled ||
    provider !== settings.provider ||
    fromAddress !== settings.fromAddress ||
    fromName !== settings.fromName ||
    (smtpHost || null) !== (settings.smtpHost || null) ||
    smtpPort !== (settings.smtpPort || 587) ||
    smtpSecure !== settings.smtpSecure ||
    (smtpUser || null) !== (settings.smtpUser || null) ||
    smtpPassword !== "";

  const handleSave = () => {
    if (formInvalid) return;
    updateSettings({
      variables: {
        input: {
          enabled,
          provider,
          fromAddress: fromAddress.trim(),
          fromName: fromName.trim(),
          smtpHost: provider === "SMTP" ? smtpHost.trim() : null,
          smtpPort: provider === "SMTP" ? smtpPort : null,
          smtpSecure: provider === "SMTP" ? smtpSecure : false,
          smtpUser: provider === "SMTP" ? smtpUser.trim() : null,
          // Only send the password when the admin actually typed a new one.
          ...(smtpPassword !== "" && { smtpPassword }),
        },
      },
    });
  };

  const testEmailInvalid =
    testEmail.trim() !== "" && !EMAIL_REGEX.test(testEmail.trim());

  const handleSendTest = () => {
    if (testEmail.trim() === "" || testEmailInvalid) return;
    sendTest({
      variables: {
        input: {
          to: testEmail.trim(),
          templateKey: testTemplate === "none" ? null : testTemplate,
        },
      },
    });
  };

  // Status banner content
  const renderStatusBanner = () => {
    if (provider === "RESEND") {
      if (settings.resendConfigured) {
        return (
          <div className="flex items-start gap-2 rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-medium text-green-700 dark:text-green-300">
                Resend skonfigurowany
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Klucz API jest dostępny w zmiennych środowiskowych.
              </p>
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">
              Brak <code className="font-mono">RESEND_API_KEY</code> w env vars
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ustaw w Railway → Variables na produkcji.
            </p>
          </div>
        </div>
      );
    }
    // SMTP provider
    if (settings.smtpConfigured) {
      return (
        <div className="flex items-start gap-2 rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <p className="font-medium text-green-700 dark:text-green-300">
              SMTP gotowy
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {settings.smtpUser
                ? `Wysyłka jako ${settings.smtpUser}.`
                : "Bez logowania (np. lokalny Mailpit)."}{" "}
              Login i hasło ustawiasz w polach poniżej.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm">
        <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
        <div>
          <p className="font-medium text-red-700 dark:text-red-300">
            Uzupełnij host i port
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Podaj dane serwera SMTP poniżej (host, port oraz login i hasło
            skrzynki — opcjonalne dla lokalnego Mailpita).
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Status dostawcy</CardTitle>
          <CardDescription>
            Bieżący stan konfiguracji wybranego dostawcy maili
          </CardDescription>
        </CardHeader>
        <CardContent>{renderStatusBanner()}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ustawienia ogólne</CardTitle>
          <CardDescription>
            Włącz lub wyłącz wysyłkę powiadomień i wybierz dostawcę
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="emailEnabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300"
            />
            <div className="space-y-1">
              <Label
                htmlFor="emailEnabled"
                className="text-base font-semibold cursor-pointer"
              >
                Powiadomienia mailowe aktywne
              </Label>
              <p className="text-xs text-muted-foreground">
                Gdy wyłączone, żadne maile nie są wysyłane — wpisy w historii
                otrzymują status „Pominięte".
              </p>
            </div>
          </div>

          {SHOW_SMTP && (
            <div className="space-y-2 max-w-md">
              <Label htmlFor="provider" className="text-base font-semibold">
                Dostawca
              </Label>
              <Select
                value={provider}
                onValueChange={(v) => setProvider(v as EmailProvider)}
              >
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESEND">Resend (zalecane)</SelectItem>
                  <SelectItem value="SMTP">Własny serwer SMTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="fromAddress" className="text-base font-semibold">
                Adres nadawcy
              </Label>
              <Input
                id="fromAddress"
                type="email"
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value)}
                placeholder="noreply@firma.pl"
              />
              {fromAddressEmpty && (
                <p className="text-xs text-red-600">Pole wymagane</p>
              )}
              {fromAddressInvalid && (
                <p className="text-xs text-red-600">
                  Niepoprawny format adresu email
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="fromName" className="text-base font-semibold">
                Nazwa nadawcy
              </Label>
              <Input
                id="fromName"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="WorkFlow"
              />
              {fromNameEmpty && (
                <p className="text-xs text-red-600">Pole wymagane</p>
              )}
            </div>
          </div>

          {SHOW_SMTP && provider === "SMTP" && (
            <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                Konfiguracja SMTP
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor="smtpHost">Host</Label>
                  <Input
                    id="smtpHost"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.example.com"
                  />
                  {smtpHostEmpty && (
                    <p className="text-xs text-red-600">Pole wymagane</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="smtpPort">Port</Label>
                  <Input
                    id="smtpPort"
                    type="number"
                    min={1}
                    max={65535}
                    value={smtpPort}
                    onChange={(e) =>
                      setSmtpPort(parseInt(e.target.value) || 0)
                    }
                  />
                  {smtpPortInvalid && (
                    <p className="text-xs text-red-600">
                      Port musi być w zakresie 1–65535
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="smtpSecure"
                  checked={smtpSecure}
                  onChange={(e) => setSmtpSecure(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="smtpSecure" className="cursor-pointer">
                  Połączenie szyfrowane (TLS/SSL)
                </Label>
                <Tooltip
                  content={
                    <div className="max-w-xs">
                      Włącz dla portu 465 (SMTPS). <br />
                      Dla portu 587 (STARTTLS) zazwyczaj <br />
                      pozostaw wyłączone.
                    </div>
                  }
                  side="top"
                >
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="smtpUser">Login (adres skrzynki)</Label>
                  <Input
                    id="smtpUser"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                    placeholder="np. workflow@firma.pl"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Pozostaw puste, jeśli serwer nie wymaga logowania.
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="smtpPassword">Hasło</Label>
                  <Input
                    id="smtpPassword"
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder={
                      settings.smtpPasswordSet
                        ? "•••••••• (zapisane — wpisz, aby zmienić)"
                        : "wpisz hasło skrzynki"
                    }
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-muted-foreground">
                    Hasło jest szyfrowane i nigdy nie jest wyświetlane ponownie.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 pt-2">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving || formInvalid}
              className="flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? "Zapisywanie..." : "Zapisz zmiany"}
            </Button>
            {hasChanges && !formInvalid && (
              <span className="text-sm text-amber-600">
                Masz niezapisane zmiany
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mail testowy</CardTitle>
          <CardDescription>
            Wyślij testową wiadomość, aby sprawdzić poprawność konfiguracji
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="testEmail">Adres odbiorcy</Label>
              <Input
                id="testEmail"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
              {testEmailInvalid && (
                <p className="text-xs text-red-600">
                  Niepoprawny format adresu email
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="testTemplate">Szablon (opcjonalnie)</Label>
              <Select value={testTemplate} onValueChange={setTestTemplate}>
                <SelectTrigger id="testTemplate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Domyślny test</SelectItem>
                  {TEMPLATE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={handleSendTest}
            disabled={
              sending ||
              testEmail.trim() === "" ||
              testEmailInvalid ||
              !enabled
            }
            variant="outline"
            className="flex items-center gap-2"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sending ? "Wysyłanie..." : "Wyślij testowy mail"}
          </Button>
          {!enabled && (
            <p className="text-xs text-amber-600">
              Powiadomienia są wyłączone — włącz je, aby wysyłać maile testowe.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Informacje</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            Ostatnia aktualizacja:{" "}
            {new Date(settings.updatedAt).toLocaleString("pl-PL")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
